import API from 'utils/api';
import { onError, onSuccess } from 'utils/handlers';

export const baseURI = 'nominas/calendarios-de-nominas';
export const periodosURI = 'nominas/calendarios-de-nominas-periodos';
export const funcionalidadesURI = 'nominas/funcionalidades-de-calendario-de-nomina';

const results = (data) => data?.results || data || [];

// ---------- Calendarios ----------

/**
 * GET Calendarios de Nóminas
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getCalendariosDeNominas = async (params) => {
  try {
    const response = await API.get(baseURI, { params });
    return results(response.data);
  } catch (err) {
    onError(err);
  }
  return [];
};

export const getCalendarioDeNomina = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const postCalendarioDeNomina = async (values) => {
  try {
    const response = await API.post(baseURI, values);
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const patchCalendarioDeNomina = async (id, values) => {
  try {
    const response = await API.patch(`${baseURI}/${id}`, values);
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const deleteCalendarioDeNomina = async (id) => {
  try {
    const response = await API.delete(`${baseURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
  }
  return false;
};

/**
 * POST Genera los periodos del calendario a partir de los Periodos de Nómina
 * @param {number} id
 * @returns {Promise<object|null>}
 */
export const generarPeriodosCalendario = async (id) => {
  try {
    const response = await API.post(`${baseURI}/${id}/generar-periodos`);
    onSuccess(response, response.data?.detail || 'Periodos generados correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * GET Periodos de Nómina del ejercicio/periodicidad que aún no están en el calendario
 * @param {number} id
 * @returns {Promise<object[]>}
 */
export const getPeriodosDeNominaDisponibles = async (id) => {
  try {
    const response = await API.get(`${baseURI}/${id}/periodos-de-nomina-disponibles`);
    return results(response.data);
  } catch (err) {
    onError(err);
  }
  return [];
};

// ---------- Configuración general ----------

export const getConfiguracionCalendario = async () => {
  try {
    const response = await API.get(`${baseURI}/configuracion`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const patchConfiguracionCalendario = async (values) => {
  try {
    const response = await API.patch(`${baseURI}/configuracion`, values);
    onSuccess(response, 'Configuración guardada correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * GET Fecha de cierre = fecha de pago - días hábiles de bloqueo
 * (los del calendario o, si no se indica, los de la configuración general)
 * @param {string} fechaDePago YYYY-MM-DD
 * @param {number} [calendarioId]
 * @returns {Promise<string|null>}
 */
export const getFechaDeCierre = async (fechaDePago, calendarioId) => {
  try {
    const response = await API.get(`${baseURI}/calcular-fecha-de-cierre`, {
      params: { fecha_de_pago: fechaDePago, calendario_de_nomina: calendarioId },
    });
    return response.data?.fecha_de_cierre || null;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * GET Fechas propuestas (apertura, cierre, entrega, pago) y nombre para un Periodo de Nómina
 * @param {number} calendarioId
 * @param {number} periodoDeNominaDetalleId
 * @returns {Promise<object|null>}
 */
export const getFechasPropuestas = async (calendarioId, periodoDeNominaDetalleId) => {
  try {
    const response = await API.get(`${baseURI}/${calendarioId}/proponer-fechas`, {
      params: { periodo_de_nomina_detalle: periodoDeNominaDetalleId },
    });
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

/**
 * GET Funcionalidades cerradas hoy (para avisos y modo solo lectura).
 * No muestra errores: si falla, la interfaz simplemente no se bloquea
 * y el backend sigue rechazando las escrituras.
 * @returns {Promise<object[]>}
 */
export const getBloqueosCalendario = async () => {
  try {
    const response = await API.get(`${baseURI}/bloqueos`);
    const data = results(response.data);
    // Si la respuesta no es un listado (p. ej. HTML o un objeto de error), no se bloquea nada.
    return Array.isArray(data) ? data : [];
  } catch (err) {
    return [];
  }
};

// ---------- Funcionalidades ----------

export const getFuncionalidadesCalendario = async () => {
  try {
    const response = await API.get(funcionalidadesURI);
    return results(response.data);
  } catch (err) {
    onError(err);
  }
  return [];
};

// ---------- Periodos ----------

export const getPeriodosCalendario = async (params) => {
  try {
    const response = await API.get(periodosURI, { params });
    return results(response.data);
  } catch (err) {
    onError(err);
  }
  return [];
};

export const getPeriodoCalendario = async (id) => {
  try {
    const response = await API.get(`${periodosURI}/${id}`);
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const postPeriodoCalendario = async (values) => {
  try {
    const response = await API.post(periodosURI, values);
    onSuccess(response, 'Agregado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const patchPeriodoCalendario = async (id, values) => {
  try {
    const response = await API.patch(`${periodosURI}/${id}`, values);
    onSuccess(response, 'Editado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const deletePeriodoCalendario = async (id) => {
  try {
    const response = await API.delete(`${periodosURI}/${id}`);
    onSuccess(response, 'Eliminado correctamente');
    return true;
  } catch (err) {
    onError(err);
  }
  return false;
};

export const abrirPeriodoCalendario = async (id) => {
  try {
    const response = await API.post(`${periodosURI}/${id}/abrir`);
    onSuccess(response, 'Periodo abierto correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};

export const cerrarPeriodoCalendario = async (id) => {
  try {
    const response = await API.post(`${periodosURI}/${id}/cerrar`);
    onSuccess(response, 'Periodo cerrado correctamente');
    return response.data;
  } catch (err) {
    onError(err);
  }
  return null;
};
